import { useCallback } from "react";

import { useLazyUserQuery, useUpdateUserProfileMutation } from "src/serverApi";
import { Languages, useTranslation } from "src/translations";
import { type UserProfile, UserProfileCurrencyEnum1, type UserProfileLanguageEnum1 } from "src/types";
import { getHubspotCookie } from "src/utils";

export const useUpdateProfileUser = () => {
    const [getUser] = useLazyUserQuery();
    const [updateProfile] = useUpdateUserProfileMutation();
    const { i18n } = useTranslation();

    return useCallback(
        async (partialUserProfile?: Partial<UserProfile>) => {
            const user = await getUser().unwrap();
            if (user.userProfile?.documentId) {
                const defaultCurrency = getCurrencyForLanguage(i18n.language as Languages);
                const hubspotCookieId = getHubspotCookie();
                await updateProfile({
                    profileDocumentId: user.userProfile.documentId,
                    data: {
                        lastLogin: new Date().toISOString(),
                        currency: user.userProfile.currency ?? defaultCurrency,
                        language: i18n.language as UserProfileLanguageEnum1,
                        hsCookieId: hubspotCookieId === "" ? undefined : hubspotCookieId,
                        ...partialUserProfile,
                    },
                });
            }
        },
        [getUser, i18n.language, updateProfile],
    );
};

const getCurrencyForLanguage = (language: Languages): UserProfileCurrencyEnum1 => {
    switch (language) {
        case Languages.cs:
            return UserProfileCurrencyEnum1.CZK;
        default:
            return UserProfileCurrencyEnum1.EUR;
    }
};

const PROVIDER_SIGNUP_KEY = "providerSignUp" as const;

type ProviderSignUpState = {
    isSignUp: boolean;
    termsAndPrivacyAccepted?: boolean;
    referralCode?: string;
};

export const setProviderSignUpState = (providerSignUpState: ProviderSignUpState) =>
    localStorage.setItem(PROVIDER_SIGNUP_KEY, JSON.stringify(providerSignUpState));

export const getProviderSignUpState = (): ProviderSignUpState => {
    const googleSignUpState = localStorage.getItem(PROVIDER_SIGNUP_KEY);
    if (!googleSignUpState) return { isSignUp: false };

    return JSON.parse(googleSignUpState);
};

export const clearProviderSignUpState = () => localStorage.removeItem(PROVIDER_SIGNUP_KEY);
